<style>
h2 {
  @apply leading-tight tracking-normal text-xl font-semibold text-center;
}
.wrapper {
  @apply w-full;
}
.calendar {
  @apply w-full bg-white rounded p-4 mb-4 select-none text-xl;
  box-shadow: 0 0 16px 4px rgba(0, 0, 0, 0.1);
}
.calendar-label {
  @apply leading-tight tracking-normal text-2xl font-semibold text-center;
}
.error {
  @apply px-4 mt-1 text-xs leading-none;
  color: #c4272a;
}
.chevron {
  @apply text-3xl cursor-pointer;
  color: var(--primary);
}
.chevron:hover {
  @apply opacity-75;
}
.weekday {
  @apply uppercase;
  color: #757575;
}
.day {
  @apply flex items-center justify-center cursor-pointer font-semibold;
}
.weekday,
.day {
  @apply h-10 text-sm;
}
.day.selected {
  @apply rounded-full;
  background-color: var(--primary-lighter);
  color: var(--primary);
}
.day.past {
  @apply pointer-events-none cursor-default font-normal;
  color: #757575;
}
.hours {
  @apply mt-10 grid grid-cols-3 gap-4;
}
@screen lg {
  .day,
  .weekday {
    @apply h-13 text-base;
  }
  .calendar {
    @apply p-6;
  }
  .calendar-label {
    @apply text-3xl;
  }
}
</style>

<script>
import { onMount } from "svelte"
import {
  format,
  isSameDay,
  startOfDay,
  eachDayOfInterval,
  isSameMonth,
  startOfMonth,
  endOfMonth,
  addMonths,
  differenceInCalendarMonths,
} from "date-fns"
import { fr, enUS as en } from "date-fns/locale"

import SubHeading from "./SubHeading.svelte"
import Button from "./Button.svelte"
import CheckButton from "./_CheckButton.svelte"

import { workflowLang } from "@shared/store/workflowKey"
import { weekdaysText } from "@shared/utils/translations"

const today = startOfDay(new Date())

export { _errors as errors }
export let value
export let className = ""
export let availableSchedules
export let noSlotsAvailableMessage = ""

let todayHours = []
let tomorrowHours = []

let showCalendar = true

const weekdays = [7, 1, 2, 3, 4, 5, 6]
let weekdaysLabels = weekdaysText[$workflowLang]
let localeLang = $workflowLang === "en" ? en : fr //for date-fns

let calendarDay = today
let calendarDelta = 0
let calendarMonth = null
let calendarLabel = null
let calendarOffset = null
let calendarDays = []
let calendarHours = []
let _errors = []

$: refreshCalendar(calendarDelta, $workflowLang)
$: refreshCalendarHours(calendarDay)

$: errors = _errors || []
$: hasErrors = errors.length > 0

function formatSlot(slot) {
  if (!slot) return
  return `${slot.startsAt}~${slot.endsAt}`
}

onMount(function () {
  if (availableSchedules.length > 0) {
    calendarDelta = differenceInCalendarMonths(
      new Date(availableSchedules[0].startsAt),
      today
    )

    localeLang = $workflowLang === "en" ? en : fr

    calendarMonth = startOfMonth(addMonths(today, calendarDelta))
    calendarLabel = format(calendarMonth, "MMMM yyyy", { locale: localeLang })

    const start = calendarMonth
    const end = endOfMonth(calendarMonth)
    calendarDays = eachDayOfInterval({ start, end })

    const selectedDate = value
      ? new Date(value.split("~")[0])
      : new Date(availableSchedules[0].startsAt)

    calendarDay = calendarDays.find((day) => isSameDay(day, selectedDate))

    const firstWeekDay = parseInt(format(calendarMonth, "i")) + 1
    calendarOffset = firstWeekDay === 8 ? 0 : firstWeekDay

    if (!value) {
      value = formatSlot(availableSchedules[0])
    }
  }
})

function hasSlots(date) {
  return availableSchedules.some((slot) =>
    isSameDay(date, new Date(slot.startsAt))
  )
}

function refreshCalendar(calendarDelta) {
  calendarHours = []

  localeLang = $workflowLang === "en" ? en : fr
  weekdaysLabels = weekdaysText[$workflowLang]
  calendarMonth = startOfMonth(addMonths(today, calendarDelta))
  calendarLabel = format(calendarMonth, "MMMM yyyy", { locale: localeLang })

  const start = calendarMonth
  const end = endOfMonth(calendarMonth)
  calendarDays = eachDayOfInterval({ start, end })

  const firstWeekDay = parseInt(format(calendarMonth, "i")) + 1
  calendarOffset = firstWeekDay === 8 ? 0 : firstWeekDay

  if (availableSchedules.length > 0) {
    const selectedDate = value
      ? new Date(value.split("~")[0])
      : new Date(availableSchedules[0].startsAt)

    if (isSameMonth(selectedDate, calendarMonth)) {
      calendarDay = calendarDays.find((day) => isSameDay(day, selectedDate))
    }
  }
}

function refreshCalendarHours(calendarDay) {
  calendarHours = availableSchedules.filter((slot) =>
    isSameDay(calendarDay, new Date(slot.startsAt))
  )
}

function openCalendar() {
  if (value) {
    for (let day of calendarDays) {
      if (isSameDay(day, new Date(value))) {
        calendarDay = day
        break
      }
    }
  }
  showCalendar = true
}

function changeCalendarDay(date) {
  calendarDay = date
  value = null
}
</script>

<div class="wrapper {className}">
  {#if showCalendar}
    <div class="calendar">
      <div class="flex items-center justify-between mb-6">
        <div class="calendar-label">{calendarLabel}</div>
        <div class="flex items-center">
          <i class="chevron material-icons" on:click="{() => calendarDelta--}">
            chevron_left
          </i>
          <i class="chevron material-icons" on:click="{() => calendarDelta++}">
            chevron_right
          </i>
        </div>
      </div>
      <div class="grid grid-cols-7 gap-1 text-center sm:gap-2">
        {#each weekdays as weekday}
          <div class="weekday">{weekdaysLabels[weekday - 1]}</div>
        {/each}
        {#each calendarDays as date, i}
          <div
            class="day"
            style="{i === 0 ? `grid-column-start: ${calendarOffset}` : ''}"
            class:selected="{isSameDay(date, calendarDay)}"
            class:past="{!hasSlots(date)}"
            on:click="{() => changeCalendarDay(date)}"
          >
            {i + 1}
          </div>
        {/each}
      </div>
    </div>
    {#if hasErrors}
      <div class="error">{errors[0]}</div>
    {/if}
    {#if availableSchedules && availableSchedules.length > 0}
      <div class="hours">
        {#each calendarHours as slot, i}
          <CheckButton
            type="radio"
            id="{`hours-${i}`}"
            value="{formatSlot(slot)}"
            bind:group="{value}"
            label="{format(new Date(slot.startsAt), 'h:mm aaa')}"
          />
        {/each}
      </div>
    {:else}
      <h2 class="mt-8">{noSlotsAvailableMessage}</h2>
    {/if}
  {:else}
    <SubHeading content="{'Today'}" className="mb-4" />
    <div class="mb-8 hours">
      {#each todayHours as hour, i}
        <CheckButton
          type="radio"
          id="{`today-hours-${i}`}"
          value="{hour}"
          bind:group="{value}"
          label="{format(new Date(hour), 'h:mm aaa')}"
        />
      {/each}
    </div>
    <SubHeading content="{'Tomorrow'}" className="mb-4" />
    <div class="hours">
      {#each tomorrowHours as hour, i}
        <CheckButton
          type="radio"
          id="{`tomorrow-hours-${i}`}"
          value="{hour}"
          bind:group="{value}"
          label="{format(new Date(hour), 'h:mm aaa')}"
        />
      {/each}
    </div>
  {/if}
  {#if !showCalendar}
    <div class="mt-16">
      <Button
        type="button"
        on:click="{openCalendar}"
        filled="{false}"
        color="primary"
        rightIcon="east"
        label="View all availability"
      />
    </div>
  {/if}
</div>
